import { memo } from 'react'
import type { Diamond } from '../api/types'
import { StoneImage } from './StoneImage'
import { SupplierName } from './StoneActions'
import { displayStockId } from '../lib/format'
import { PriceBlock } from './PriceBlock'
import { FinishStrip, SpecMeta } from './StoneSpecs'
import { PriceMark, usePeer } from './StonePeer'

interface Props {
  stone: Diamond
  onOpen?: (s: Diamond) => void
  blurPrice?: boolean
  /** Position in the grid — drives the peer mark's entrance stagger. */
  index?: number
  /** Share/compare selection (2026-09-20): present ⇒ a tick control renders
   *  on the card, so CARD view can feed the same Create-share bar the table's
   *  row checkboxes do. Absent ⇒ the card is exactly what it was. */
  selected?: boolean
  onToggleSelect?: () => void
}

/**
 * Web-native grid card for a diamond. Mobile uses a row layout;
 * web has horizontal real estate so we go vertical card with the
 * media on top, specs + price below. Tap (or click) opens the
 * detail modal; the inline + button adds without leaving the grid.
 *
 * Same visual identity as the mobile DiamondRow — navy + white,
 * rounded-card, a real StoneImage photo on top, 360 chip if a
 * video exists.
 */
export const DiamondCard = memo(function DiamondCard({ stone, onOpen, blurPrice, index = 0, selected = false, onToggleSelect }: Props) {
  // Defensive read — old Firestore docs that pre-date the `media`
  // schema rename don't carry the nested object, so `stone.media`
  // can be undefined. StoneImage handles the image fallback chain.
  const videoUrl = stone.media?.videoUrl ?? null
  // Server withholds prices until the caller is verified (KYC).
  const priceLocked = stone.priceUSD == null || stone.pricePerCarat == null
  // Where this stone's $/ct sits among the comparable stones on Diaex. Null
  // for an anonymous or unverified viewer (the server withholds the bands
  // with the prices) and for any bucket too thin to state a position about —
  // in which case nothing renders at all, never a placeholder.
  const peer = usePeer(stone, 1)

  // The tick control is a SIBLING of the card button (nested interactive
  // elements are invalid HTML), absolutely positioned over the media corner.
  return (
    <div className={`relative h-full w-full ${selected ? 'rounded-lg ring-2 ring-gold-500 ring-offset-1' : ''}`}>
    {onToggleSelect && (
      <button
        type="button"
        role="checkbox"
        aria-checked={selected}
        aria-label={selected ? 'Remove from selection' : 'Select for share or compare'}
        onClick={(e) => { e.stopPropagation(); onToggleSelect() }}
        data-testid="card-select"
        className={`absolute top-2 right-2 z-10 grid h-7 w-7 place-items-center rounded-full border shadow-elev-1 transition-colors ${
          selected ? 'border-gold-500 bg-gold-500 text-white' : 'border-border bg-paper/95 text-soft backdrop-blur-sm hover:text-ink'
        }`}
      >
        {selected ? (
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><path d="m5 13 4 4L19 7" /></svg>
        ) : (
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round"><path d="M12 5v14M5 12h14" /></svg>
        )}
      </button>
    )}
    <button
      type="button"
      onClick={() => onOpen?.(stone)}
      className="panel panel-hover group w-full h-full flex flex-col items-stretch text-left rounded-lg overflow-hidden hover:-translate-y-1 active:scale-[0.99]"
      data-testid="diamond-card"
      data-stock-id={stone.stockId}
    >
      <div className="relative aspect-square w-full overflow-hidden bg-gradient-to-br from-[#ffffff] to-[#eef2f7]">
        {/* Image zoom-on-hover — the stone breathes toward the buyer as the
            card lifts. Clipped by the media box's overflow-hidden. */}
        <StoneImage
          stone={stone}
          width={512}
          imgClassName="absolute inset-0 h-full w-full object-cover transition-transform duration-[240ms] ease-premium group-hover:scale-[1.06]"
        />
        {stone.tripleEx && (
          <span className="absolute top-2 left-2 rounded-sm bg-navy-700 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-gold-300">
            3EX
          </span>
        )}
        {!!videoUrl && (
          <span className="absolute bottom-2 right-2 inline-flex items-center gap-1 rounded-full bg-paper/95 px-2 py-1 text-[10px] font-bold text-sapphire shadow-elev-1 backdrop-blur-sm">
            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 2v6M12 16v6M2 12h6M16 12h6" />
              <circle cx="12" cy="12" r="3.5" />
            </svg>
            360°
          </span>
        )}
      </div>

      <div className="p-3 space-y-1">
        {/* Spec line leads (2026-08-04): the trade identifies a stone by
            shape · weight · grade, not by our internal id — the id drops to
            a quiet mono second line. Format is load-bearing for the unit
            tests ("Round · 1.25ct · D VS1"). */}
        <div className="flex items-baseline justify-between gap-2">
          <p className="text-[13.5px] font-semibold text-ink truncate tracking-h3">
            {stone.shape} · {Number(stone.carat || 0).toFixed(2)}ct{stone.color ? ` · ${stone.color}` : ''}{stone.clarity ? ` ${stone.clarity}` : ''}
          </p>
          {stone.lab && (
            /* Ledger: blue = trust. The lab certification is the trust signal,
               so the chip carries the sapphire accent (gold stays value-only). */
            <span className="shrink-0 rounded-sm border border-sapphire/40 bg-sapphire/10 px-1.5 py-px font-mono text-[9px] font-semibold text-sapphire uppercase tracking-wider">
              {stone.lab}
            </span>
          )}
        </div>
        <p className="text-[11px] text-navy-300 truncate mono-id">#{displayStockId(stone)}</p>

        {/* ── The finish + proportions block (2026-08-11) ──────────────────
            Cut/Polish/Symmetry as codes over fill bars, then measurements and
            the two derived proportions. This is the detail Tej asked the cards
            to carry; the bars are what let a buyer read a whole grid of them
            without parsing letters. Shape-aware: a fancy shape shows two marks
            rather than a dash where a cut grade would be, because labs do not
            issue one for fancies. */}
        <div className="flex items-end justify-between gap-2 pt-1.5">
          <FinishStrip stone={stone} />
          {stone.fluorescence && (
            <span className="shrink-0 text-[10px] leading-none text-navy-300">
              <span className="sr-only">Fluorescence: </span>
              {stone.fluorescence === 'None' ? 'No fluor.' : `${stone.fluorescence} fluor.`}
            </span>
          )}
        </div>
        <SpecMeta stone={stone} className="text-[10.5px] leading-snug text-navy-300 truncate" />

        {/* Supplier + stone origin — #35 / Vikram 2026-07-26.
            The supplier line is ALWAYS rendered now, via the shared
            <SupplierName>, which is the same component the table uses. It used
            to render only when `stone.supplier?.name` was present — but the
            server withholds that block from anyone who is not KYC-approved, so
            an anon or pending viewer silently saw a bare origin and had no idea
            a supplier was being withheld. That threw away the strongest reason
            to finish verification, and it read as "this stone has no supplier"
            rather than "you can't see it yet". The card gets the roomy register
            (no `compact`) because it has the width for the full sentence. */}
        <p className="text-[11px] text-navy-300 truncate flex items-center gap-1">
          <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="shrink-0 text-navy-300" aria-hidden><path d="M3 21h18M5 21V7l7-4 7 4v14M9 9h.01M9 13h.01M9 17h.01M15 9h.01M15 13h.01M15 17h.01" /></svg>
          <SupplierName stone={stone} signedOut={!!blurPrice} className="truncate" />
          {stone.origin && <span className="truncate shrink-0"> · {stone.origin}</span>}
        </p>

        <div className="flex items-center justify-between pt-2 mt-1 border-t border-line">
          <div className="leading-tight">
            {/* $/carat is the primary axis the trade compares on (Vikram,
                2026-07-26). PriceBlock handles the three states: anon
                sign-in prompt, verification-pending lock, or the price. */}
            <PriceBlock stone={stone} blurPrice={blurPrice} mult={1} />
          </div>
        </div>

        {/* THE MARKET POSITION. A card has the vertical room a table row does
            not, so this is the one browse surface that spells the read out in
            words. "12% under 61 comparable" — never "below market", never
            "deal": the denominator is ours and we name it. */}
        {!blurPrice && !priceLocked && peer && (
          <PriceMark stone={stone} peer={peer} variant="card" index={index} className="pt-1.5" />
        )}
      </div>
    </button>
    </div>
  )
})
